/**
 * Width tokens for the site chrome (header + footer).
 *
 * These mirror the section width vocabulary in globalProps.ts so a header or
 * footer set to "wide" lines up with a "wide" page section. The max-width
 * values are the pixel equivalents of the STANDARD_WIDTH Tailwind tokens
 * (max-w-6xl / max-w-7xl):
 *   contained → 72rem (1152px) — matches a contained section
 *   wide      → 80rem (1280px) — matches a wide section
 *   full      → edge-to-edge (no max-width)
 *
 * Header and footer each carry their own token, set independently in the
 * chrome editor. Shared by the live site render (Navbar/Footer) and the
 * portal editor's canvas preview so both stay in lockstep.
 */

export type ChromeWidth = "contained" | "wide" | "full";

export const CHROME_WIDTHS: ChromeWidth[] = ["contained", "wide", "full"];

export const CHROME_WIDTH_LABELS: Record<ChromeWidth, string> = {
  contained: "Contained",
  wide: "Wide",
  full: "Full",
};

/** Max-width per token, aligned to STANDARD_WIDTH (globalProps.ts). */
export const CHROME_MAX_WIDTH: Record<ChromeWidth, string> = {
  contained: "72rem",
  wide: "80rem",
  full: "none",
};

/** Coerce an unknown stored value to a valid token, defaulting to contained. */
export function normalizeChromeWidth(value: unknown): ChromeWidth {
  return value === "wide" || value === "full" ? value : "contained";
}

/** Inline style for the chrome's inner max-width container. */
export function chromeContainerStyle(value: unknown): { maxWidth: string } {
  return { maxWidth: CHROME_MAX_WIDTH[normalizeChromeWidth(value)] };
}
